import type { TrackStyle } from './stylePresets.ts';

export type GoogleFontCatalogEntry = {
  id: string;
  family: string;
  category: string;
  /** Normalized Google Fonts license code from METADATA.pb */
  license: 'OFL' | 'APACHE2' | 'UFL' | string;
  subsets: string[];
  primaryScript: string | null;
  familiesParam: string;
  cssProperties: string;
  fallback: string;
};

export type GoogleFontSubsetPreview = {
  main: string;
  second: string;
  thumb: string;
};

export type GoogleFontSubsetOption = {
  subset: string;
  /** Browser i18n / Style Studio message key for the English/UI name */
  nameKey: string;
  /** English fallback name */
  en: string;
  /** Autonym / native script name (like Azure nativeName). */
  nativeName: string;
  /** Preview copy for Style Studio stage + card thumbs. */
  preview: GoogleFontSubsetPreview;
};

type CatalogFile = {
  generatedAt?: string;
  count?: number;
  fonts: GoogleFontCatalogEntry[];
};

/** Same CDN root as kuromoji-dict (netflixsubs-app-downloads). */
export const GOOGLE_FONTS_CATALOG_URL =
  'https://netflixsubs.app/scache/netflixsubs-app-downloads/google-fonts-catalog.json';

let fonts: GoogleFontCatalogEntry[] = [];
let familiesParamIndex: Map<string, GoogleFontCatalogEntry> | null = null;
let loadPromise: Promise<boolean> | null = null;

function setFonts(next: readonly GoogleFontCatalogEntry[]): void {
  fonts = [...next];
  familiesParamIndex = null;
}

/** Test helper — inject a local catalog without network. */
export function setGoogleFontsCatalogForTests(
  entries: readonly GoogleFontCatalogEntry[],
): void {
  loadPromise = null;
  setFonts(entries);
}

export function getGoogleFontsCatalog(): readonly GoogleFontCatalogEntry[] {
  return fonts;
}

export function isGoogleFontsCatalogLoaded(): boolean {
  return fonts.length > 0;
}

/**
 * Fetch catalog from netflixsubs-app-downloads (not bundled).
 * Safe to call multiple times — shares one in-flight promise.
 */
export function loadGoogleFontsCatalog(
  fetchImpl: (input: string, init?: RequestInit) => Promise<Response>,
): Promise<boolean> {
  if (fonts.length > 0) return Promise.resolve(true);
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const res = await fetchImpl(GOOGLE_FONTS_CATALOG_URL, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as CatalogFile;
      if (!Array.isArray(data?.fonts) || data.fonts.length === 0) {
        throw new Error('empty catalog');
      }
      setFonts(data.fonts);
      return true;
    } catch {
      loadPromise = null;
      setFonts([]);
      return false;
    }
  })();

  return loadPromise;
}

const LATIN_PREVIEW: GoogleFontSubsetPreview = {
  main: 'Hello, how are you?',
  second: 'Nice to meet you.',
  thumb: 'Aa',
};

/**
 * Curated language subsets shown in Style Studio (labels start with uppercase).
 * Raw GF tags like `adlam` / `linear-a` are omitted from the Language filter.
 */
export const GOOGLE_FONT_SUBSET_OPTIONS: ReadonlyArray<GoogleFontSubsetOption> = [
  {
    subset: 'arabic',
    nameKey: 'style_studio_google_subset_arabic',
    en: 'Arabic',
    nativeName: 'العربية',
    preview: {
      main: 'مرحبا، كيف حالك؟',
      second: 'تشرفت بمقابلتك.',
      thumb: 'مرح',
    },
  },
  {
    subset: 'bengali',
    nameKey: 'style_studio_google_subset_bengali',
    en: 'Bengali',
    nativeName: 'বাংলা',
    preview: {
      main: 'হ্যালো, আপনি কেমন আছেন?',
      second: 'আপনার সাথে দেখা করে ভালো লাগল।',
      thumb: 'বাং',
    },
  },
  {
    subset: 'chinese-simplified',
    nameKey: 'style_studio_google_subset_chinese_simplified',
    en: 'Chinese Simplified',
    nativeName: '中文 (简体)',
    preview: {
      main: '我们还会再见吗？',
      second: 'Will we meet again?',
      thumb: '简体',
    },
  },
  {
    subset: 'chinese-traditional',
    nameKey: 'style_studio_google_subset_chinese_traditional',
    en: 'Chinese Traditional',
    nativeName: '繁體中文',
    preview: {
      main: '我們還會再見嗎？',
      second: 'Will we meet again?',
      thumb: '繁中',
    },
  },
  {
    subset: 'cyrillic',
    nameKey: 'style_studio_google_subset_cyrillic',
    en: 'Cyrillic',
    nativeName: 'Кириллица',
    preview: {
      main: 'Привет, как дела?',
      second: 'Рад тебя видеть.',
      thumb: 'Аа',
    },
  },
  {
    subset: 'cyrillic-ext',
    nameKey: 'style_studio_google_subset_cyrillic_ext',
    en: 'Cyrillic Extended',
    nativeName: 'Кириллица+',
    preview: {
      main: 'Привіт, як справи?',
      second: 'Радий тебе бачити.',
      thumb: 'Аа',
    },
  },
  {
    subset: 'devanagari',
    nameKey: 'style_studio_google_subset_devanagari',
    en: 'Devanagari',
    nativeName: 'देवनागरी',
    preview: {
      main: 'नमस्ते, आप कैसे हैं?',
      second: 'आपसे मिलकर अच्छा लगा।',
      thumb: 'नम',
    },
  },
  {
    subset: 'greek',
    nameKey: 'style_studio_google_subset_greek',
    en: 'Greek',
    nativeName: 'Ελληνικά',
    preview: {
      main: 'Γεια σου, τι κάνεις;',
      second: 'Χάρηκα για τη γνωριμία.',
      thumb: 'Αα',
    },
  },
  {
    subset: 'greek-ext',
    nameKey: 'style_studio_google_subset_greek_ext',
    en: 'Greek Extended',
    nativeName: 'Ελληνικά+',
    preview: {
      main: 'Γεια σου, τι κάνεις;',
      second: 'Χάρηκα για τη γνωριμία.',
      thumb: 'Αα',
    },
  },
  {
    subset: 'hebrew',
    nameKey: 'style_studio_google_subset_hebrew',
    en: 'Hebrew',
    nativeName: 'עברית',
    preview: {
      main: 'שלום, מה שלומך?',
      second: 'נעים להכיר.',
      thumb: 'עב',
    },
  },
  {
    subset: 'japanese',
    nameKey: 'style_studio_google_subset_japanese',
    en: 'Japanese',
    nativeName: '日本語',
    preview: {
      main: 'また会えるといいね。',
      second: 'Hope we meet again.',
      thumb: 'あア',
    },
  },
  {
    subset: 'korean',
    nameKey: 'style_studio_google_subset_korean',
    en: 'Korean',
    nativeName: '한국어',
    preview: {
      main: '다시 만날 수 있을까?',
      second: 'Will we meet again?',
      thumb: '한글',
    },
  },
  {
    subset: 'latin',
    nameKey: 'style_studio_google_subset_latin',
    en: 'Latin',
    nativeName: 'Latin',
    preview: { ...LATIN_PREVIEW },
  },
  {
    subset: 'latin-ext',
    nameKey: 'style_studio_google_subset_latin_ext',
    en: 'Latin Extended',
    nativeName: 'Latin+',
    preview: {
      main: 'Dzień dobry, co słychać?',
      second: 'Miło cię poznać.',
      thumb: 'Ąę',
    },
  },
  {
    subset: 'tamil',
    nameKey: 'style_studio_google_subset_tamil',
    en: 'Tamil',
    nativeName: 'தமிழ்',
    preview: {
      main: 'வணக்கம், எப்படி இருக்கிறீர்கள்?',
      second: 'உங்களை சந்தித்ததில் மகிழ்ச்சி.',
      thumb: 'தமி',
    },
  },
  {
    subset: 'thai',
    nameKey: 'style_studio_google_subset_thai',
    en: 'Thai',
    nativeName: 'ไทย',
    preview: {
      main: 'สวัสดี คุณเป็นอย่างไรบ้าง?',
      second: 'ยินดีที่ได้รู้จัก',
      thumb: 'ไทย',
    },
  },
  {
    subset: 'vietnamese',
    nameKey: 'style_studio_google_subset_vietnamese',
    en: 'Vietnamese',
    nativeName: 'Tiếng Việt',
    preview: {
      main: 'Xin chào, bạn khỏe không?',
      second: 'Rất vui được gặp bạn.',
      thumb: 'Vi',
    },
  },
];

/** @deprecated Use GOOGLE_FONT_SUBSET_OPTIONS; kept for callers expecting a map. */
export const GOOGLE_FONT_SUBSET_LABELS: Record<string, string> = Object.fromEntries(
  GOOGLE_FONT_SUBSET_OPTIONS.map((o) => [o.subset, o.en]),
);

function catalogHasSubset(subset: string): boolean {
  return getGoogleFontsCatalog().some((font) => font.subsets.includes(subset));
}

/** Curated subsets present in the catalog (Language filter options). */
export function listGoogleFontSubsets(): string[] {
  return GOOGLE_FONT_SUBSET_OPTIONS
    .filter((o) => catalogHasSubset(o.subset))
    .map((o) => o.subset);
}

export function getGoogleFontSubsetOption(subset: string): GoogleFontSubsetOption | undefined {
  return GOOGLE_FONT_SUBSET_OPTIONS.find((o) => o.subset === subset);
}

/** Azure-style label: localized/English name + nativeName. */
export function formatGoogleFontSubsetLabel(name: string, nativeName: string): string {
  if (!nativeName || nativeName === name) return name;
  return `${name} (${nativeName})`;
}

/** Short display label for catalog license codes. */
export function formatGoogleFontLicense(license: string | null | undefined): string {
  switch ((license || '').toUpperCase()) {
    case 'OFL':
      return 'OFL';
    case 'APACHE2':
      return 'Apache 2.0';
    case 'UFL':
      return 'Ubuntu Font Licence';
    default:
      return license?.trim() || '';
  }
}

export function previewForGoogleFontSubset(subset: string | null | undefined): GoogleFontSubsetPreview {
  const opt = subset ? getGoogleFontSubsetOption(subset) : undefined;
  return opt?.preview ?? LATIN_PREVIEW;
}

export function filterGoogleFonts(opts: {
  subset?: string | null;
  query?: string | null;
}): GoogleFontCatalogEntry[] {
  const subset = opts.subset?.trim() || null;
  const query = (opts.query || '').trim().toLowerCase();

  return getGoogleFontsCatalog().filter((font) => {
    if (subset && !font.subsets.includes(subset)) return false;
    if (query && !font.family.toLowerCase().includes(query) && !font.id.includes(query)) {
      return false;
    }
    return true;
  });
}

/** Page size for Style Studio Google Fonts grid (avoids Latin DOM freeze). */
export const GOOGLE_FONT_GRID_PAGE_SIZE = 48;

/** Slice filtered fonts for incremental grid rendering. */
export function pageGoogleFonts<T>(
  fonts: readonly T[],
  limit: number,
): { visible: T[]; hasMore: boolean; total: number } {
  const safeLimit = Math.max(0, Math.floor(limit));
  return {
    visible: fonts.slice(0, safeLimit),
    hasMore: fonts.length > safeLimit,
    total: fonts.length,
  };
}

export function catalogEntryToFontFields(
  entry: GoogleFontCatalogEntry,
): Pick<TrackStyle, 'fontGoogleFamilies' | 'fontGoogleCssProperties'> {
  return {
    fontGoogleFamilies: entry.familiesParam,
    fontGoogleCssProperties: entry.cssProperties,
  };
}

function getFamiliesParamIndex(): Map<string, GoogleFontCatalogEntry> {
  if (!familiesParamIndex) {
    familiesParamIndex = new Map(
      getGoogleFontsCatalog().map((font) => [font.familiesParam, font]),
    );
  }
  return familiesParamIndex;
}

export function getGoogleFontByFamiliesParam(
  familiesParam: string | null | undefined,
): GoogleFontCatalogEntry | undefined {
  const key = (familiesParam || '').trim();
  if (!key) return undefined;
  return getFamiliesParamIndex().get(key);
}
